import { createAsyncThunk } from "@reduxjs/toolkit";
import { ColumnFiltersState } from "@tanstack/react-table";
import { DistrictInChargeAxios } from "api/DistrictInChargeAxios";
import axios from "axios";

export interface StorageLocation {
  id?: number;
  StorageLocationName: string;
  state: string;
  status: boolean;
  createBy?: string;
  updatedBy?: string;
  createDate?: string;
  updateDate?: string;
}
export interface PaginatedResponse<T> {
  count: number;
  total_pages: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export const fetchStorageLocations = createAsyncThunk(
  "storageLocation/fetchStorageLocations",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
  }) => {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("page_size", pageSize.toString());
      if (search) params.append("search", search);
      if (sort) {
        const orderingValue = order === "desc" ? `-${sort}` : sort;
        params.append("ordering", orderingValue);
      }

      if (filters && filters.length > 0) {
        // params.append("filters", JSON.stringify(filters));
        filters.forEach(
          ({
            filterField,
            filterValue,
          }: {
            filterField: string;
            filterValue: any;
          }) => {
            if (
              filterField &&
              filterValue !== undefined &&
              filterValue !== null
            ) {
              if (typeof filterValue === "object" && filterValue !== null) {
                // filterField is expected to be either 'created_at' or 'updated_at'
                Object.entries(filterValue).forEach(([key, value]) => {
                  if (value !== undefined && value !== null && value !== "") {
                    const dateKey = `${filterField}_${
                      key === "from_date" ? "from" : "to"
                    }`;
                    params.append(dateKey, String(value));
                  }
                });
              } else {
                // Handle primitive values (string, number)
                if (filterField === "covered") {
                  // If filterValue is "Covered", convert to true
                  // If it's "Not Covered", convert to false
                  console.log(
                    `filterValue: ${filterValue} filterField: ${filterField}`
                  );
                  const booleanValue =
                    filterValue === "covered"
                      ? true
                      : filterValue === "uncovered"
                      ? false
                      : filterValue;
                  params.append(filterField, String(booleanValue));
                } else {
                  params.append(filterField, filterValue);
                }
              }
            }
          }
        );
      }

      //   const response: any = await DistrictInChargeAxios.get(
      //     `/StorageLocation/?${params.toString()}`
      //   );
      const response: any = await DistrictInChargeAxios.get(
        `/district_storage_location/list_filter/?${params.toString()}`
      );
      // const response: any = await DistrictInChargeAxios.get(`/district/`, {
      //   params: {
      //     page,
      //     page_size: pageSize,
      //     search: search || undefined,
      //     filters: filters ? JSON.stringify(filters) : undefined,
      //   },
      // });

      // response.data;
      return {
        data: response.data.data || [],
        // data: response.data || [],
        rowCount: response.data.count || 0,
        // rowCount: response.count || 0,
      };
    } catch (error: any) {
      throw new Error("Failed to fetch Storage Locations");
    }
  }
);

export const fetchOnlyAllStorageLocations = createAsyncThunk(
  "",
  async (id: string, { rejectWithValue }) => {
    try {
      const response: any = await DistrictInChargeAxios.get("/StorageLocation");
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch StorageLocation by ID"
      );
    }
  }
);

export const fetchStorageLocationById = createAsyncThunk(
  "StorageLocation/fetchById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await DistrictInChargeAxios.get(`/StorageLocation/${id}/get/`);
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch StorageLocation by ID"
      );
    }
  }
);

export const fetchStorageLocationByDropDown = createAsyncThunk(
  "StorageLocation/fetchByDropDown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await DistrictInChargeAxios.get(`/StorageLocation/dropdown/`);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch StorageLocation list"
      );
    }
  }
);

export const updateStorageLocation = createAsyncThunk(
  "StorageLocation/updateStorageLocation",
  async ({
    id,
    StorageLocationData,
  }: {
    id: string;
    StorageLocationData: any;
  }) => {
    const response: any = await DistrictInChargeAxios.put(
      `/district_storage_location/update/${id}/`,
      StorageLocationData
    );
    return response;
  }
);

export const updateStatusInStorageLocation = createAsyncThunk(
  "StorageLocation/updateStatusInStorageLocation",
  async (StorageLocationData: any, { rejectWithValue }) => {
    const payload = {
      is_active: StorageLocationData.is_active,
    };
    try {
      //   const response: any = await DistrictInChargeAxios.patch(
      //     `/district_storage_location/status/${StorageLocationData.id}/`,
      //     payload
      //   );
      const response: any = await DistrictInChargeAxios.patch(
        `/district_storage_location/status/${StorageLocationData.id}/`,
        payload
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to update status");
    }
  }
);

export const addStorageLocation = createAsyncThunk(
  "StorageLocation/addStorageLocation",
  async (StorageLocationData: any, { rejectWithValue }) => {
    try {
      const response = await DistrictInChargeAxios.post(
        `/district_storage_location/create/`,
        StorageLocationData
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to add StorageLocation"
      );
    }
  }
);

export const exportStorageLocationCSV = async ({
  search,
  filters,
}: {
  search?: string;
  filters?: any;
}) => {
  const params = new URLSearchParams();

  params.append("export", "excel");

  if (search) params.append("search", search);

  if (filters && filters.length > 0) {
    filters.forEach(
      ({
        filterField,
        filterValue,
      }: {
        filterField: string;
        filterValue: any;
      }) => {
        if (filterField && filterValue !== undefined && filterValue !== null) {
          if (typeof filterValue === "object" && filterValue !== null) {
            Object.entries(filterValue).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== "") {
                params.append(key, String(value));
              }
              if (
                (filterValue.from_date || filterValue.to_date) &&
                (filterField === "created_at" || filterField === "updated_at")
              ) {
                if (filterField === "created_at") {
                  filterField = "created";
                } else {
                  filterField = "updated";
                }
                params.append("filter_by", filterField);
              }
            });
          } else {
            params.append(filterField, filterValue);
          }
        }
      }
    );
  }

  const response = await DistrictInChargeAxios.get(
    `/district_storage_location/list_filter/?${params.toString()}`,
    {
      responseType: "blob",
    }
  );

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || "StorageLocation.csv",
  };
};
